/**
 * Query keys and the few hooks every screen shares. Feature screens add their own hooks
 * beside them, using `keys` so invalidation stays consistent.
 */
import { useQuery } from '@tanstack/react-query'
import { api, groupPath } from './client'
import type { Home, Me, Notification } from './types'

export const keys = {
  me: ['me'] as const,
  group: (groupId: string) => ['group', groupId] as const,
  home: (groupId: string) => ['group', groupId, 'home'] as const,
  notifications: (groupId: string) => ['group', groupId, 'notifications'] as const,
  loans: (groupId: string) => ['group', groupId, 'loans'] as const,
  loan: (groupId: string, loanId: string) => ['group', groupId, 'loans', loanId] as const,
  activity: (groupId: string) => ['group', groupId, 'activity'] as const,
  payment: (groupId: string, paymentId: string) => ['group', groupId, 'payments', paymentId] as const,
  approvals: (groupId: string) => ['group', groupId, 'approvals'] as const,
  statements: (groupId: string) => ['group', groupId, 'statements'] as const,
  dashboard: (groupId: string) => ['group', groupId, 'dashboard'] as const,
}

export function useMe(enabled = true) {
  return useQuery({ queryKey: keys.me, queryFn: () => api.get<Me>('/me'), enabled, staleTime: 60_000 })
}

export function useHome(groupId: string | null) {
  return useQuery({
    queryKey: keys.home(groupId ?? 'none'),
    queryFn: () => api.get<Home>(groupPath(groupId as string, 'home')),
    enabled: Boolean(groupId),
    refetchOnMount: 'always',
  })
}

export function useNotifications(groupId: string | null) {
  return useQuery({
    queryKey: keys.notifications(groupId ?? 'none'),
    queryFn: () => api.get<{ notifications: Notification[]; unread: number }>(
      groupPath(groupId as string, 'notifications'), undefined, { background: true }),
    enabled: Boolean(groupId),
    refetchInterval: 60_000,
  })
}
